<script setup>
import { computed, ref } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useMapGetter } from 'dashboard/composables/store';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import VisionPixCard from './VisionPixCard.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { toast } from 'dashboard/helper/toast';
import { extractPixCodes, injectPixChips, PIX_CHIP_ATTR } from './visionPix';
import { extractBoletoLinks, boletoFilename } from './visionBoleto';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const hasEmptyMessageContent = computed(() => !props.message?.content);
const hasAsset = computed(
  () => props.message?.boleto_url || props.message?.pix_copia_cola
);

// Códigos PIX despejados no texto viram chips curtos que copiam ao clicar
// (ver visionPix.js). O card estruturado (message.pix_copia_cola) segue à parte.
const extracted = computed(() => extractPixCodes(props.message.content));

const messageContent = computed(() => {
  const formatter = new MessageFormatter(extracted.value.content);
  return injectPixChips(formatter.formattedMessage, extracted.value.codes, tail =>
    t('VISION.PIX_CHIP', { tail })
  );
});

const onContentClick = async event => {
  const chip = event.target.closest?.(`[${PIX_CHIP_ATTR}]`);
  if (!chip) return;
  const code = extracted.value.codes[Number(chip.getAttribute(PIX_CHIP_ATTR))];
  if (!code) return;
  try {
    await copyTextToClipboard(code);
    toast.success(t('VISION.PIX_COPIED'));
  } catch (error) {
    // Clipboard bloqueado (aba sem foco, permissão) — divulga, não engole.
    toast.error(`${t('VISION.PIX_COPY_ERROR')} ${error.message}`);
  }
};

// Boletos anexáveis: o estruturado (tool_result → message.boleto_url, caminho
// IXC) + os links de boleto que o LLM escreveu no texto (caminho SGP inline).
const boletoLinks = computed(() => {
  const links = extractBoletoLinks(props.message.content);
  const structured = props.message.boleto_url;
  if (structured && !links.some(link => link.url === structured)) {
    links.unshift({ url: structured, label: '' });
  }
  return links;
});

const attachingUrl = ref(null);

const attachBoleto = async url => {
  if (attachingUrl.value) return;
  attachingUrl.value = url;
  try {
    const boletoUrl = new URL(url, window.location.origin);
    const accessToken = currentUser.value?.access_token;
    const headers =
      boletoUrl.origin === window.location.origin && accessToken
        ? { api_access_token: accessToken }
        : {};
    const res = await fetch(boletoUrl, { headers, redirect: 'follow' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const name = boletoFilename(boletoUrl.href);
    const file = new File([blob], name, { type: 'application/pdf' });
    emitter.emit(BUS_EVENTS.VISION_ATTACH_FILE, {
      name,
      type: 'application/pdf',
      size: file.size,
      file,
    });
    toast.success(t('VISION.BOLETO_ATTACHED'));
  } catch (error) {
    // CORS/404 do provedor: divulga o motivo, o link segue clicável no texto.
    toast.error(`${t('VISION.BOLETO_ATTACH_ERROR')} ${error.message}`);
  } finally {
    attachingUrl.value = null;
  }
};

// Vários boletos na mesma resposta: distingue pelo rótulo do link ou pelo
// segmento da URL (nº do documento no SGP).
const boletoButtonLabel = link => {
  if (boletoLinks.value.length === 1) return t('VISION.BOLETO_ATTACH');
  const hint = link.label || boletoFilename(link.url).replace(/^boleto-|\.pdf$/g, '');
  return `${t('VISION.BOLETO_ATTACH')} · ${hint}`;
};
</script>

<template>
  <div class="flex flex-col gap-1 text-n-slate-12">
    <div class="font-medium">{{ $t('VISION.NAME') }}</div>
    <span v-if="hasEmptyMessageContent && !hasAsset" class="text-n-ruby-11">
      {{ $t('VISION.EMPTY_MESSAGE') }}
    </span>
    <!--
      Tipografia de leitura (prose-bubble = a mesma das mensagens da conversa,
      theme-aware). Tabela de títulos rola na horizontal dentro do painel em vez
      de quebrar célula por célula — `break-words` fica só fora da tabela.
    -->
    <div
      v-else-if="!hasEmptyMessageContent"
      v-dompurify-html="messageContent"
      class="prose prose-bubble prose-sm max-w-none min-w-0 break-words [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_ul]:my-1 [&_li]:my-0 [&_p]:my-1 [&_table]:block [&_table]:overflow-x-auto [&_table]:whitespace-nowrap [&_table]:text-xs [&_table]:my-2 [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_td]:align-top"
      @click="onContentClick"
    />
    <VisionPixCard
      v-if="message.pix_copia_cola"
      :code="message.pix_copia_cola"
    />
    <div class="flex flex-row gap-2 mt-1">
      <Button
        v-for="link in boletoLinks"
        :key="link.url"
        :label="
          attachingUrl === link.url
            ? $t('VISION.BOLETO_ATTACHING')
            : boletoButtonLabel(link)
        "
        :disabled="!!attachingUrl"
        faded
        sm
        slate
        icon="i-lucide-paperclip"
        @click="attachBoleto(link.url)"
      />
    </div>
  </div>
</template>
